/**
 * Утилита для надежного ожидания загрузки Telegram WebApp API
 */

/**
 * Ожидает полной загрузки Telegram WebApp API
 * @param {number} maxWaitTime - Максимальное время ожидания в миллисекундах (по умолчанию 5000)
 * @param {number} checkInterval - Интервал проверки в миллисекундах (по умолчанию 100)
 * @returns {Promise<boolean>} - true если API загружен, false если таймаут
 */
export const waitForTelegramAPI = (maxWaitTime = 5000, checkInterval = 100) => {
    return new Promise((resolve) => {
        const startTime = Date.now();
        
        const checkAPI = () => {
            // Проверяем наличие всех необходимых компонентов Telegram WebApp API
            const hasTelegram = window.Telegram !== undefined;
            const hasWebApp = window.Telegram?.WebApp !== undefined;
            const hasShareMessage = window.Telegram?.WebApp?.shareMessage !== undefined;
            const hasOpenTelegramLink = window.Telegram?.WebApp?.openTelegramLink !== undefined;
            const hasReady = window.Telegram?.WebApp?.ready !== undefined;
            
            const isFullyLoaded = hasTelegram && hasWebApp && hasShareMessage && hasOpenTelegramLink && hasReady;
            
            if (isFullyLoaded) {
                console.log('✅ Telegram WebApp API полностью загружен');
                resolve(true);
                return;
            }
            
            // Проверяем таймаут
            if (Date.now() - startTime >= maxWaitTime) {
                console.warn('⚠️ Таймаут ожидания Telegram WebApp API');
                resolve(false);
                return;
            }
            
            // Продолжаем проверку
            setTimeout(checkAPI, checkInterval);
        };
        
        // Начинаем проверку
        checkAPI();
    });
};

/**
 * Безопасный вызов shareMessage с ожиданием загрузки API
 * @param {string} messageId - ID подготовленного сообщения
 * @param {Function} callback - Callback функция
 * @returns {Promise<boolean>} - true если вызов успешен, false если ошибка
 */
export const safeShareMessage = async (messageId, callback = null) => {
    try {
        // Ждем загрузки API
        const isLoaded = await waitForTelegramAPI();
        
        if (!isLoaded) {
            console.error('❌ Telegram WebApp API не загружен');
            if (callback) callback(false);
            return false;
        }
        
        // Проверяем наличие shareMessage
        if (typeof window.Telegram.WebApp.shareMessage !== 'function') {
            console.error('❌ shareMessage не доступен');
            if (callback) callback(false);
            return false;
        }
        
        // Telegram кэширует shareMessage для одного messageId
        // Простое решение: перезагружаем страницу для сброса состояния
        console.log('🔄 Telegram кэширует shareMessage, перезагружаем страницу для сброса');
        
        // Добавляем параметр для перезагрузки
        const currentUrl = window.location.href;
        const reloadUrl = currentUrl.includes('?') 
            ? `${currentUrl}&reload=${Date.now()}` 
            : `${currentUrl}?reload=${Date.now()}`;
        
        // Перезагружаем страницу
        window.location.href = reloadUrl;
        
        return true;
    } catch (error) {
        console.error('❌ Ошибка в safeShareMessage:', error);
        if (callback) callback(false);
        return false;
    }
};

/**
 * Безопасный вызов openTelegramLink с ожиданием загрузки API
 * @param {string} url - URL для открытия
 * @returns {Promise<boolean>} - true если вызов успешен, false если ошибка
 */
export const safeOpenTelegramLink = async (url) => {
    try {
        // Ждем загрузки API
        const isLoaded = await waitForTelegramAPI();
        
        if (!isLoaded) {
            console.error('❌ Telegram WebApp API не загружен');
            return false;
        }
        
        // Проверяем наличие openTelegramLink
        if (typeof window.Telegram.WebApp.openTelegramLink !== 'function') {
            console.error('❌ openTelegramLink не доступен');
            return false;
        }
        
        // Вызываем openTelegramLink
        window.Telegram.WebApp.openTelegramLink(url);
        return true;
    } catch (error) {
        console.error('❌ Ошибка в safeOpenTelegramLink:', error);
        return false;
    }
};

/**
 * Проверяет, загружен ли Telegram WebApp API
 * @returns {boolean} - true если API загружен, false если нет
 */
export const isTelegramAPILoaded = () => {
    return !!(
        window.Telegram?.WebApp &&
        typeof window.Telegram.WebApp.shareMessage === 'function' &&
        typeof window.Telegram.WebApp.openTelegramLink === 'function' &&
        typeof window.Telegram.WebApp.ready === 'function'
    );
};
